import {
    Button,
    Card,
    FormCheck,
    FormSelect,
    Modal,
    Nav,
    OverlayTrigger,
    Tab,
    Tooltip,
} from '@goproblems/ui';
import {
    CommentGenreEnum,
    ProblemComment,
    UserMinimal,
    VotableEntityTypeEnum,
} from '@types';
import {isMobile} from 'react-device-detect';

import {CommentVoteButtons, MarkdownContent, MdEditor} from '@components';
import {forwardRef, useEffect, useMemo, useRef, useState} from 'react';
import {UserItem} from './UserItem';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCrosshairs, faTrash} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import {
    useCreateCommentMutation,
    useDeleteCommentMutation,
    useFetchBulkVoteInfoMutation,
} from '@api';
import {
    findNodeFromCompressedPath,
    useAddCompressedPath,
    useDispatch,
    useSelector,
} from '@utils';
import {boardSlice} from '@reducers';
import {useTranslation} from 'react-i18next';
import type {CommentVoteInfo} from '@components/CommentVoteButtons';
import {useTheme} from '@components/theme/useTheme';

// Add CSS animation for highlight flash
const highlightAnimation = `
@keyframes highlight-flash {
    0% {
        background-color: var(--highlight-bg-normal);
        border-color: var(--highlight-border-normal);
    }
    25% {
        background-color: var(--highlight-bg-deep);
        border-color: var(--highlight-border-deep);
    }
    50% {
        background-color: var(--highlight-bg-deep);
        border-color: var(--highlight-border-deep);
    }
    100% {
        background-color: var(--highlight-bg-normal);
        border-color: var(--highlight-border-normal);
    }
}
`;

const CommentItem = forwardRef<
    HTMLDivElement,
    {
        comment: ProblemComment;
        showGoToPosition?: boolean;
        onGoToPosition?: (path: string) => void;
        onDelete?: () => void;
        canDelete?: boolean;
        isHighlighted?: boolean;
        voteInfo?: CommentVoteInfo | null;
        onVoteChange?: (commentId: number, voteInfo: CommentVoteInfo) => void;
    }
>(
    (
        {
            comment,
            showGoToPosition,
            onGoToPosition,
            onDelete,
            canDelete,
            isHighlighted,
            voteInfo,
            onVoteChange,
        },
        ref
    ) => {
        const {t} = useTranslation();
        const theme = useTheme();
        const [showDeleteModal, setShowDeleteModal] = useState(false);
        const [isAnimating, setIsAnimating] = useState(false);

        const isDisabled = comment ? !comment.alive : false;

        const commentTypeLabel = (() => {
            switch (comment.type) {
                case CommentGenreEnum.QUESTION:
                    return t('single_problem.comment_genre.question');
                case CommentGenreEnum.CORRECT:
                    return t('single_problem.comment_genre.correct_type');
                case CommentGenreEnum.WRONG:
                    return t('single_problem.comment_genre.wrong_type');
                default:
                    return null;
            }
        })();

        // Trigger animation when highlighted
        useEffect(() => {
            if (isHighlighted) {
                setIsAnimating(true);
                const timer = setTimeout(() => {
                    setIsAnimating(false);
                }, 800); // Animation duration: 0.8 seconds
                return () => clearTimeout(timer);
            }
            return undefined;
        }, [isHighlighted]);

        // Theme-aware highlight colors
        const normalBgColor =
            theme === 'dark' ? 'rgba(156, 163, 175, 0.2)' : '#fefce8';
        const normalBorderColor = theme === 'dark' ? '#a3a3a3' : '#fbbf24';
        const deepBgColor =
            theme === 'dark' ? 'rgba(156, 163, 175, 0.4)' : '#fef3c7';
        const deepBorderColor = theme === 'dark' ? 'var(--ui-text-muted)' : '#f59e0b';

        const highlightStyles = isHighlighted
            ? {
                  backgroundColor: normalBgColor,
                  borderColor: normalBorderColor,
                  borderWidth: '1px',
              }
            : {
                  borderColor: 'var(--ui-border)',
                  borderWidth: '1px',
              };

        const handleDeleteClick = () => {
            setShowDeleteModal(true);
        };

        const handleConfirmDelete = () => {
            if (onDelete) {
                onDelete();
            }
            setShowDeleteModal(false);
        };

        return (
            <>
                <style>{highlightAnimation}</style>
                <Card
                    ref={ref}
                    className={`px-1.5 pt-1.5 pb-2 rounded-none ${
                        isHighlighted ? 'shadow-lg' : ''
                    }`}
                    style={{
                        borderTop: 'none',
                        transition: 'all 0.5s ease',
                        // allow overlay icon positioning
                        position: 'relative',
                        ...highlightStyles,
                        ...(isAnimating && {
                            animation: 'highlight-flash 0.8s ease-out',
                            '--highlight-bg-normal': normalBgColor,
                            '--highlight-border-normal': normalBorderColor,
                            '--highlight-bg-deep': deepBgColor,
                            '--highlight-border-deep': deepBorderColor,
                        }),
                        // Visual treatment for disabled comments
                        ...(isDisabled && {
                            opacity: 0.65,
                            filter: 'grayscale(70%)',
                            backgroundColor:
                                theme === 'dark'
                                    ? 'rgba(255,255,255,0.02)'
                                    : 'var(--ui-surface-muted)',
                        }),
                    }}>
                    <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 text-xs">
                        <UserItem
                            avatarClassName="!ml-0"
                            user={comment.author}
                            showLevel
                            textClassName="!mx-2 gap-1 text-xs !font-normal"
                        />
                        <div
                            className={`ml-auto grid shrink-0 items-center justify-items-center gap-1 ${
                                showGoToPosition
                                    ? 'grid-cols-[1.25rem_1.25rem_auto]'
                                    : 'grid-cols-[1.25rem_auto]'
                            }`}>
                            <span className="inline-flex h-5 w-5 items-center justify-center">
                                {canDelete && comment.alive && (
                                    <button
                                        type="button"
                                        className="inline-flex h-5 w-5 items-center justify-center border-0 bg-transparent p-0 text-neutral-500 transition-colors hover:text-red-600 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                                        aria-label={t(
                                            'single_problem.delete_comment'
                                        )}
                                        title={t(
                                            'single_problem.delete_comment'
                                        )}
                                        onClick={handleDeleteClick}>
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            aria-hidden
                                        />
                                    </button>
                                )}
                            </span>
                            {showGoToPosition && (
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip
                                            id={`go-to-comment-${comment.id}`}>
                                            {t(
                                                'single_problem.go_to_position'
                                            )}
                                        </Tooltip>
                                    }>
                                    <button
                                        type="button"
                                        className="inline-flex h-5 w-5 items-center justify-center border-0 bg-transparent p-0 text-neutral-500 transition-colors hover:text-neutral-900 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 dark:text-neutral-400 dark:hover:text-neutral-100"
                                        aria-label={t(
                                            'single_problem.go_to_position'
                                        )}
                                        onClick={() =>
                                            onGoToPosition?.(
                                                comment.compressedPath
                                            )
                                        }>
                                        <FontAwesomeIcon
                                            icon={faCrosshairs}
                                            aria-hidden
                                        />
                                    </button>
                                </OverlayTrigger>
                            )}
                            <span className="justify-self-end whitespace-nowrap pl-1 tabular-nums text-neutral-500 dark:text-neutral-400">
                                {dayjs(comment.createdAt).format(
                                    'YYYY-MM-DD hh:mm:ss'
                                )}
                            </span>
                        </div>
                    </div>
                    <div
                        style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            paddingBottom: '18px',
                        }}>
                        <MarkdownContent
                            text={comment.text}
                            preserveLineBreaks
                        />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                        <CommentVoteButtons
                            entityType={VotableEntityTypeEnum.COMMENT}
                            entityId={comment.id}
                            voteInfo={voteInfo || null}
                            onVoteChange={onVoteChange}
                        />
                        {(commentTypeLabel || isDisabled) && (
                            <div className="flex items-center gap-2 text-[11px] font-normal leading-none text-neutral-400 dark:text-neutral-500">
                                {commentTypeLabel && (
                                    <span>{commentTypeLabel}</span>
                                )}
                                {isDisabled && (
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        aria-label={t(
                                            'single_problem.disabled_comment'
                                        )}
                                        title={t(
                                            'single_problem.disabled_comment'
                                        )}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </Card>

                {/* Delete Confirmation Modal */}
                <Modal
                    show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)}
                    size="sm"
                    centered>
                    <Modal.Header closeButton>
                        <Modal.Title className="text-sm">
                            {t('single_problem.delete_comment_modal_title')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="text-sm">
                        {t('single_problem.delete_comment_modal_body')}
                    </Modal.Body>
                    <Modal.Footer className="text-sm">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowDeleteModal(false)}>
                            {t('single_problem.cancel')}
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleConfirmDelete}>
                            {t('single_problem.disable')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
);

CommentItem.displayName = 'CommentItem';

export const ProblemCommentsCard = ({
    className,
    problemId,
    style,
    comments,
    maxContentHeight,
    highlightedCommentPath,
    highlightedCommentId,
    onSubmit,
    showInputForm = true,
    problemAuthor,
}: {
    className?: string;
    style?: React.CSSProperties;
    problemId: number;
    comments?: ProblemComment[];
    maxContentHeight?: number;
    highlightedCommentPath?: string | null;
    highlightedCommentId?: number | null;
    onSubmit?: () => void;
    showInputForm?: boolean;
    problemAuthor?: UserMinimal;
}) => {
    const {user} = useSelector(s => s.auth);
    const {root, compressedPath} = useSelector(s => s.board);
    const [createComment] = useCreateCommentMutation();
    const [deleteComment] = useDeleteCommentMutation();
    const [getBulkVoteInfo] = useFetchBulkVoteInfoMutation();
    const [genre, setGenre] = useState<CommentGenreEnum>(
        CommentGenreEnum.COMMENT
    );
    const [activeKey, setActiveKey] = useState('local');
    const [comment, setComment] = useState<string>('');
    const [showDisabledComments, setShowDisabledComments] =
        useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [voteInfos, setVoteInfos] = useState<Record<number, CommentVoteInfo>>(
        {}
    );
    const dispatch = useDispatch();
    const [addCompressedPath] = useAddCompressedPath();
    const {t} = useTranslation();
    const commentRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
    const localScrollContainerRef = useRef<HTMLDivElement | null>(null);

    // Start with the most recent commenter, then other commenters, then problem author
    const contextUsers = useMemo(() => {
        const users: UserMinimal[] = [];
        const seen = new Set<number>();

        // Add comment authors in reverse order (most recent first)
        if (comments) {
            for (let i = comments.length - 1; i >= 0; i--) {
                const author = comments[i].author;
                if (!seen.has(author.id)) {
                    seen.add(author.id);
                    users.push(author);
                }
            }
        }

        // Add problem author last
        if (problemAuthor && !seen.has(problemAuthor.id)) {
            users.push(problemAuthor);
        }

        return users;
    }, [comments, problemAuthor]);

    // Add CSS for pulse animation - only once
    useEffect(() => {
        // Check if style already exists to avoid duplicates
        if (document.getElementById('comment-pulse-animation')) {
            return;
        }

        const style = document.createElement('style');
        style.id = 'comment-pulse-animation';
        style.textContent = `
            @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.02); }
                100% { transform: scale(1); }
            }
        `;
        document.head.appendChild(style);

        return () => {
            const existingStyle = document.getElementById(
                'comment-pulse-animation'
            );
            if (existingStyle) {
                document.head.removeChild(existingStyle);
            }
        };
    }, []);

    useEffect(() => {
        if (!highlightedCommentId || !comments) return;

        let targetComment;

        if (highlightedCommentId) {
            targetComment = comments.find(
                comment => comment.id === highlightedCommentId
            );
        } else {
            targetComment = comments.find(
                comment => comment.compressedPath === highlightedCommentPath
            );
        }

        if (!targetComment) return;

        if (!targetComment.alive) {
            setShowDisabledComments(true);
        }

        const timeoutId = setTimeout(() => {
            if (localScrollContainerRef.current) {
                const container = localScrollContainerRef.current;
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'smooth',
                });
            }

            const commentKey = highlightedCommentId
                ? highlightedCommentId.toString()
                : targetComment.compressedPath;
            const commentElement = commentRefs.current[commentKey];
            if (commentElement) {
                setTimeout(() => {
                    commentElement.style.animation = 'pulse 1.5s ease-in-out';
                }, 800);
            }
        }, 200);

        return () => clearTimeout(timeoutId);
    }, [
        highlightedCommentPath,
        highlightedCommentId,
        comments,
        compressedPath,
    ]);

    const handleSubmitComment = async () => {
        if (!user || !comment.trim() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await createComment({
                prob: problemId,
                genre,
                path: compressedPath,
                authorid: user.id,
                comment,
            }).unwrap();
            setComment('');
        } catch (error) {
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
        if (onSubmit) {
            onSubmit();
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.ctrlKey && event.key === 'Enter') {
            event.preventDefault();
            void handleSubmitComment();
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        try {
            await deleteComment({
                problemId,
                commentId,
            }).unwrap();
            if (onSubmit) onSubmit();
        } catch (error) {
            console.error('Failed to delete comment:', error);
        }
    };

    const commentIds = useMemo(() => {
        return comments?.map(c => c.id) ?? [];
    }, [comments]);

    useEffect(() => {
        const loadVoteInfos = async () => {
            if (commentIds.length === 0 || !user) return;

            try {
                const result = await getBulkVoteInfo({
                    entityType: VotableEntityTypeEnum.COMMENT,
                    entityIds: commentIds,
                }).unwrap();

                setVoteInfos(result);
            } catch (error) {
                console.error('Failed to load vote info:', error);
            }
        };

        void loadVoteInfos();
    }, [commentIds, user, getBulkVoteInfo]);

    const handleVoteChange = (commentId: number, voteInfo: CommentVoteInfo) => {
        setVoteInfos(prev => ({
            ...prev,
            [commentId]: voteInfo,
        }));
    };

    const disabledCommentCount = comments
        ? comments.filter(
              currentComment =>
                  !currentComment.alive &&
                  (activeKey === 'local'
                      ? currentComment.compressedPath === compressedPath
                      : true)
          ).length
        : 0;

    const visibleAllComments = comments?.filter(comment =>
        showDisabledComments ? true : comment.alive
    );

    const getTabClassName = (eventKey: 'local' | 'all') =>
        `!-mb-px !rounded-none !border-b-2 !bg-transparent !px-2.5 !py-1 !text-sm !font-medium !leading-5 focus-visible:!outline-none focus-visible:!ring-2 focus-visible:!ring-neutral-500 focus-visible:!ring-offset-1 ${
            activeKey === eventKey
                ? '!border-neutral-400 !text-neutral-800 dark:!border-neutral-400 dark:!text-neutral-200'
                : '!border-transparent !text-neutral-500 hover:!border-neutral-300 hover:!bg-transparent hover:!text-neutral-700 dark:hover:!border-neutral-600 dark:hover:!text-neutral-300'
        }`;

    return (
        <div
            className={className}
            style={{
                width: isMobile ? '100%' : '29rem',
                ...style,
            }}>
            <Tab.Container
                defaultActiveKey="local"
                activeKey={activeKey}
                onSelect={eventKey => {
                    if (eventKey) setActiveKey(eventKey);
                }}>
                <div className="flex items-center justify-between border-b border-neutral-200 dark:border-[var(--ui-border)]">
                    <Nav className="min-w-0 flex-nowrap" role="tablist">
                        <Nav.Item>
                            <Nav.Link
                                eventKey="local"
                                role="tab"
                                aria-selected={activeKey === 'local'}
                                className={getTabClassName('local')}>
                                {t('single_problem.local_comments')}
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                eventKey="all"
                                role="tab"
                                aria-selected={activeKey === 'all'}
                                className={getTabClassName('all')}>
                                {t('common.all')}
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <FormCheck
                        type="checkbox"
                        label={
                            <span className="whitespace-nowrap text-neutral-500">
                                {t('single_problem.show_disabled')}{' '}
                                <span>({disabledCommentCount})</span>
                            </span>
                        }
                        checked={showDisabledComments}
                        onChange={() =>
                            setShowDisabledComments(!showDisabledComments)
                        }
                        className="!mb-0 !min-h-0 !gap-1.5 pl-2 pr-2 [&>input]:cursor-pointer [&>input]:!accent-neutral-600 [&>input]:focus:!ring-neutral-500 dark:[&>input]:!accent-neutral-300 [&>label]:!cursor-pointer [&>label]:!text-sm [&>label]:!leading-5"
                    />
                </div>
                <Tab.Content className="!mt-0">
                    <Tab.Pane eventKey="local">
                        <div
                            ref={localScrollContainerRef}
                            className={`overflow-y-scroll ${
                                showInputForm && user
                                    ? ''
                                    : 'border-b border-neutral-200 dark:border-[var(--ui-border)]'
                            }`}
                            style={{maxHeight: maxContentHeight || 'auto'}}>
                            {comments &&
                                comments
                                    .filter(
                                        comment =>
                                            comment.compressedPath ===
                                                compressedPath &&
                                            (showDisabledComments
                                                ? true
                                                : comment.alive)
                                    )
                                    .map((comment, idx) => (
                                        <CommentItem
                                            key={idx}
                                            ref={el => {
                                                if (el) {
                                                    commentRefs.current[
                                                        comment.id.toString()
                                                    ] = el;
                                                    commentRefs.current[
                                                        comment.compressedPath
                                                    ] = el;
                                                }
                                            }}
                                            comment={comment}
                                            isHighlighted={
                                                highlightedCommentId
                                                    ? highlightedCommentId ===
                                                      comment.id
                                                    : highlightedCommentPath ===
                                                      comment.compressedPath
                                            }
                                            onDelete={() =>
                                                handleDeleteComment(comment.id)
                                            }
                                            canDelete={
                                                user?.id === comment.author.id
                                            }
                                            voteInfo={voteInfos[comment.id]}
                                            onVoteChange={handleVoteChange}
                                        />
                                    ))}
                            {showInputForm && (
                                <div className="mt-2 px-2 pb-2 text-xs sm:px-0 sm:pb-0">
                                    {user ? (
                                        <>
                                            <span className="mb-1 block font-semibold">
                                                {t(
                                                    'single_problem.add_comment_to_this_position'
                                                )}
                                            </span>
                                            <MdEditor
                                                value={comment}
                                                onChange={value =>
                                                    setComment(value)
                                                }
                                                placeholder={t(
                                                    'single_problem.comment_placeholder'
                                                )}
                                                onKeyDown={handleKeyDown}
                                                disabled={isSubmitting}
                                                contextUsers={contextUsers}
                                            />
                                            <div className="mt-2 flex items-center justify-between gap-2">
                                                <FormSelect
                                                    size="sm"
                                                    className="!h-9 !w-auto min-w-32 max-w-[65%] !rounded px-2 pr-8 text-sm"
                                                    defaultValue={genre}
                                                    onChange={e => {
                                                        setGenre(
                                                            e.target
                                                                .value as CommentGenreEnum
                                                        );
                                                    }}
                                                    disabled={isSubmitting}>
                                                    <option
                                                        value={
                                                            CommentGenreEnum.COMMENT
                                                        }>
                                                        {t(
                                                            'single_problem.comment_genre.comment'
                                                        )}
                                                    </option>
                                                    <option
                                                        value={
                                                            CommentGenreEnum.QUESTION
                                                        }>
                                                        {t(
                                                            'single_problem.comment_genre.question'
                                                        )}
                                                    </option>
                                                    <option
                                                        value={
                                                            CommentGenreEnum.CORRECT
                                                        }>
                                                        {t(
                                                            'single_problem.comment_genre.correct_type'
                                                        )}
                                                    </option>
                                                    <option
                                                        value={
                                                            CommentGenreEnum.WRONG
                                                        }>
                                                        {t(
                                                            'single_problem.comment_genre.wrong_type'
                                                        )}
                                                    </option>
                                                </FormSelect>
                                                <Button
                                                    className="h-9 shrink-0 rounded px-3 text-sm"
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={
                                                        handleSubmitComment
                                                    }
                                                    disabled={
                                                        isSubmitting ||
                                                        !comment.trim()
                                                    }>
                                                    {isSubmitting ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"></span>
                                                            {t(
                                                                'common.submitting'
                                                            )}
                                                        </>
                                                    ) : (
                                                        t('common.submit')
                                                    )}
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-center py-4 text-neutral-500">
                                            {t(
                                                'single_problem.sign_in_to_leave_comment'
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="all">
                        <div
                            className="overflow-y-scroll border-b border-neutral-200 dark:border-[var(--ui-border)]"
                            style={{maxHeight: maxContentHeight || 'auto'}}>
                            {visibleAllComments?.length === 0 && (
                                <div className="px-3 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
                                    {t('single_problem.no_comments_yet')}
                                </div>
                            )}
                            {visibleAllComments?.map((comment, idx) => (
                                <CommentItem
                                    key={idx}
                                    ref={el => {
                                        if (el) {
                                            // Store refs using comment ID for precise targeting
                                            commentRefs.current[
                                                comment.id.toString()
                                            ] = el;
                                            // Also store by compressed path for backward compatibility
                                            commentRefs.current[
                                                comment.compressedPath
                                            ] = el;
                                        }
                                    }}
                                    comment={comment}
                                    isHighlighted={
                                        highlightedCommentId
                                            ? highlightedCommentId === comment.id
                                            : highlightedCommentPath ===
                                              comment.compressedPath
                                    }
                                    showGoToPosition={true}
                                    onGoToPosition={compressedPath => {
                                        if (!root) return;
                                        const node = findNodeFromCompressedPath(
                                            root,
                                            compressedPath
                                        );
                                        if (node) {
                                            dispatch(
                                                boardSlice.actions.setCurrentNode(
                                                    node
                                                )
                                            );
                                        } else {
                                            addCompressedPath(compressedPath);
                                        }
                                        setActiveKey('local');
                                    }}
                                    onDelete={() =>
                                        handleDeleteComment(comment.id)
                                    }
                                    canDelete={user?.id === comment.author.id}
                                    voteInfo={voteInfos[comment.id]}
                                    onVoteChange={handleVoteChange}
                                />
                            ))}
                        </div>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default ProblemCommentsCard;
